import { useMemo } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { ALCOVE, ROOM } from "../../lib/layout";
import { ARCH } from "../../lib/world";
import { Wall } from "./Walls";
import { PERIM, SECTION_IDS, SECTIONS, zoneRanges } from "../../lib/sections";
import { dayFrag, hallFrag, hallVert } from "./hallShaders";
import { day } from "../../lib/daylight";
import { Daylight } from "./Daylight";

const aspect = (t) => (t?.image?.width ? t.image.width / t.image.height : 1);

const vivid = new WeakMap();
function vividness(t) {
  const img = t?.image;
  if (!img || !img.width) return 0;
  if (vivid.has(t)) return vivid.get(t);
  const c = document.createElement("canvas");
  c.width = c.height = 16;
  const g = c.getContext("2d");
  let v = 0;
  try {
    g.drawImage(img, 0, 0, 16, 16);
    const d = g.getImageData(0, 0, 16, 16).data;
    for (let i = 0; i < d.length; i += 4) {
      const mx = Math.max(d[i], d[i + 1], d[i + 2]), mn = Math.min(d[i], d[i + 1], d[i + 2]);
      v += mx ? (mx - mn) / mx : 0;
    }
  } catch {
    v = 0;
  }
  vivid.set(t, v);
  return v;
}

export function useHallUniforms(groups, texById, fallbackTex, lite, birdsTex, sideTex = []) {
  const u = useMemo(() => {
    const u = { uTime: { value: 0 }, uLite: { value: 0 }, uW: { value: ROOM.W }, uD: { value: ROOM.D }, uH: { value: ROOM.H }, uP: { value: PERIM } };
    const aspA = [];
    const aspB = [];
    SECTION_IDS.forEach((id, k) => {
      const list = groups[id];
      const a = texById[list[0]?.id] || fallbackTex;
      const b = texById[list[1]?.id] || a;
      u[`uA${k}`] = { value: a };
      u[`uB${k}`] = { value: b };
      u[`uTint${k}`] = { value: new THREE.Color(SECTIONS[id].tint) };
      u[`uS${k}`] = { value: new THREE.Vector2(...zoneRanges[k]) };
      aspA[k] = aspect(a);
      aspB[k] = aspect(b);
    });
    u.uAspA = { value: aspA };
    u.uAspB = { value: aspB };
    // projection pool: the first two paintings of each feeling, cycled every ~50s with a 6s crossfade
    // plus the two birds ("Love must feel like this"), shown second so it comes round early
    // most colourful first, so the hall opens on vivid light rather than pale, milky paintings
    const base = [...new Set(SECTION_IDS.flatMap((_, k) => [u[`uA${k}`].value, u[`uB${k}`].value]))].sort((a, b) => vividness(b) - vividness(a) + (sideTex.includes(a) ? 1e6 : 0) - (sideTex.includes(b) ? 1e6 : 0));
    const pool = birdsTex ? [base[0], birdsTex, ...base.slice(1).filter((t) => t !== birdsTex)] : base;
    Object.defineProperties(u, { pool: { value: pool, enumerable: false }, cycle: { value: { t: 0, i: 1 }, enumerable: false } });
    window.__afaeProjBirds = birdsTex ? pool.indexOf(birdsTex) : -1;
    // side walls: Priyanka's picks first (left "Simmering passion", right "The butterfly wing"), then they rotate;
    // the three surfaces never show the same painting at once
    const n = pool.length, k = Math.max(1, Math.floor(n / 3));
    const rot = (o) => pool.map((_, j) => pool[(j + o) % n]);
    const seqs = [pool, [sideTex[0], ...rot(k)].filter(Boolean), [sideTex[1], ...rot(2 * k)].filter(Boolean)];
    const all = [...new Set(seqs.flat())];
    const slots = (i) => {
      const used = [];
      seqs.forEach((s) => {
        let j = i;
        while (used.includes(s[j % s.length]) && j < i + s.length) j++;
        used.push(s[j % s.length]);
      });
      return used;
    };
    Object.defineProperties(u, { slots: { value: slots, enumerable: false }, all: { value: all, enumerable: false } });
    [u.uPA, u.uQA, u.uRA] = u.slots(0).map((value) => ({ value }));
    [u.uPB, u.uQB, u.uRB] = u.slots(1).map((value) => ({ value }));
    u.uPMix = { value: 0 };
    u.uProjOn = { value: P27_OFF ? 0 : 1 };
    u.uProjLow = { value: new URLSearchParams(window.location.search).get("p31") === "off" ? 0 : 1 };
    return Object.assign(u, day);
  }, [groups, texById, fallbackTex, birdsTex, sideTex[0], sideTex[1]]); // eslint-disable-line react-hooks/exhaustive-deps
  useFrame((_, dt) => {
    u.uTime.value = performance.now() / 1000;
    u.uLite.value = lite ? 1 : 0;
    if (!day.uMotion.value) return;
    const c = u.cycle;
    c.t += Math.min(dt, 0.5);
    u.uPMix.value = THREE.MathUtils.smoothstep(c.t, 44, 50);
    if (c.t >= 50) {
      c.t = 0;
      c.i = (c.i + 1) % u.pool.length;
      [u.uPA.value, u.uQA.value, u.uRA.value] = [u.uPB.value, u.uQB.value, u.uRB.value];
      [u.uPB.value, u.uQB.value, u.uRB.value] = u.slots(c.i);
      u.uPMix.value = 0;
    }
    const ids = [u.uPA, u.uQA, u.uRA].map((x) => x.value?.userData?.slug || u.all.indexOf(x.value));
    window.__afaeProj = { i: c.i, t: +c.t.toFixed(1), mix: +u.uPMix.value.toFixed(2), n: u.pool.length, showing: ids, distinct: new Set(ids).size === 3 };
  });
  return u;
}

// ?p27=off hides the phase 27 projections, garlands and decor, for fps/brightness comparison only
export const P27_OFF = new URLSearchParams(window.location.search).get("p27") === "off";
// ?hall=projection shows the phase 25 projection look, for side-by-side comparison only
export const OLD_HALL = new URLSearchParams(window.location.search).get("hall") === "projection";
export const hallMaterial = (u, surf, { pale = 0, alpha, over } = {}) =>
  new THREE.ShaderMaterial({
    vertexShader: hallVert,
    fragmentShader: OLD_HALL ? hallFrag : dayFrag,
    transparent: !!alpha,
    uniforms: { ...u, uSurf: { value: surf }, uPale: { value: pale }, uAlpha: alpha || { value: 1 }, uOver: { value: over || null }, uHasOver: { value: over ? 1 : 0 } },
  });

function Plane({ pos, rot = [0, 0, 0], size, mat }) {
  return (
    <mesh position={pos} rotation={rot} material={mat}>
      <planeGeometry args={size} />
    </mesh>
  );
}

export function ProjectionHall({ u, still }) {
  // The Memory Wall alcove stays dim so its letters and painting glimpses are the brightest things there.
  const mats = useMemo(() => ({ wall: hallMaterial(u, 0), wallL: hallMaterial({ ...u, uPA: u.uQA, uPB: u.uQB }, 0), wallR: hallMaterial({ ...u, uPA: u.uRA, uPB: u.uRB }, 0), floor: hallMaterial(u, 1), ceil: hallMaterial(u, 2), dim: new THREE.MeshBasicMaterial({ color: "#2A1F3D", transparent: true, opacity: 0.5, depthWrite: false, toneMapped: false }) }), [u]);
  const { W, D, H } = ROOM;
  const { w: aw, d: ad, h: ah } = ALCOVE;
  const zb = D / 2 + ad;
  return (
    <group>
      <ambientLight intensity={1.1} color="#FAF0E6" />
      <hemisphereLight args={["#FFF4E2", "#EADBC8", 1.1]} />
      {!OLD_HALL && <Daylight u={u} still={still} />}
      <Wall a={[-W / 2, -D / 2]} b={[W / 2, -D / 2]} n={[0, 1]} H={H} mat={mats.wall} />
      <Wall a={[W / 2, -D / 2]} b={[W / 2, D / 2]} n={[-1, 0]} H={H} mat={mats.wallR} />
      <Wall a={[-W / 2, -D / 2]} b={[-W / 2, D / 2]} n={[1, 0]} H={H} mat={mats.wallL} />
      <Wall a={[-W / 2, D / 2]} b={[W / 2, D / 2]} n={[0, -1]} H={H} mat={mats.wall} holes={[{ c: W / 2, w: aw, h: ah }]} />
      <group data-testid="memory-alcove">
        <Plane pos={[0, ah / 2, zb]} rot={[0, Math.PI, 0]} size={[aw, ah]} mat={mats.wall} />
        <Plane pos={[-aw / 2, ah / 2, D / 2 + ad / 2]} rot={[0, Math.PI / 2, 0]} size={[ad, ah]} mat={mats.wall} />
        <Plane pos={[aw / 2, ah / 2, D / 2 + ad / 2]} rot={[0, -Math.PI / 2, 0]} size={[ad, ah]} mat={mats.wall} />
        <Plane pos={[0, 0, D / 2 + ad / 2]} rot={[-Math.PI / 2, 0, 0]} size={[aw, ad]} mat={mats.floor} />
        <Plane pos={[0, ah, D / 2 + ad / 2]} rot={[Math.PI / 2, 0, 0]} size={[aw, ad]} mat={mats.ceil} />
        <Plane pos={[0, ah / 2, zb - 0.02]} rot={[0, Math.PI, 0]} size={[aw, ah]} mat={mats.dim} />
        <Plane pos={[-aw / 2 + 0.02, ah / 2, D / 2 + ad / 2]} rot={[0, Math.PI / 2, 0]} size={[ad, ah]} mat={mats.dim} />
        <Plane pos={[aw / 2 - 0.02, ah / 2, D / 2 + ad / 2]} rot={[0, -Math.PI / 2, 0]} size={[ad, ah]} mat={mats.dim} />
        <Plane pos={[0, 0.02, D / 2 + ad / 2]} rot={[-Math.PI / 2, 0, 0]} size={[aw, ad]} mat={mats.dim} />
        <Plane pos={[0, ah - 0.02, D / 2 + ad / 2]} rot={[Math.PI / 2, 0, 0]} size={[aw, ad]} mat={mats.dim} />
      </group>
      <Plane pos={[0, 0, 0]} rot={[-Math.PI / 2, 0, 0]} size={[W, D]} mat={mats.floor} />
      <Plane pos={[0, H, 0]} rot={[Math.PI / 2, 0, 0]} size={[W, D]} mat={mats.ceil} />
    </group>
  );
}
